import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
  GridApiPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '@/data/constants';

import triplegUtils from '@/services/triplegUtils';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '@/hooks/permissions/useIsSuperReaderUser';

import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import {
  SyncedTripLeg,
  SyncedTripLegWithCareLevel,
} from '@/data/models/reservation.model';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '@/data/models/trip-leg-status.model';

import * as lookupSlice from '@/data/lookup.slice';

import triplegService from '@/services/triplegService';

import {
  ButtonException,
  CellActions,
  CellDualValue,
  CellSingleValue,
  DataGridPro,
} from '@/components/tables/DataGridPro';
import { ExceptionResolveDialog } from '@/components/home/triplegs/exceptions-resolve/ExceptionResolveDialog';

interface MonitoringExceptionsProps {
  page: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  rowCount: number;
  handlePaginationChange: (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => void;
  handleSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => void;
  tripLegs: TripLegSearchResponseItem[];
  loading: boolean;
}

const MonitoringExceptionsTable = ({
  page,
  pageSize,
  pageSizeOptions,
  rowCount,
  handlePaginationChange,
  handleSortModelChange,
  tripLegs,
  loading,
}: MonitoringExceptionsProps) => {
  const t = useCommonTranslation();
  const navigate = useNavigate();
  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const isSuperReaderUser = useIsSuperReaderUser();

  const [loadingTripLegData, setLoadingTripLegData] = useState(false);
  const [dialogResolveOpen, setDialogResolveOpen] = useState(false);
  const [selectedTripLeg, setSelectedTripLeg] =
    useState<SyncedTripLegWithCareLevel>();

  const formatEta = useCallback(
    (eta?: number): string => {
      if (!eta) {
        return '';
      }
      return `${Math.round(eta / 60)} ${t('common.minutes')}`;
    },
    [t],
  );

  const shouldShowEta = (params: GridCellParams): boolean => {
    const tripLegStatus = params.row.tripLegStatusBC?.toLowerCase();
    return tripStatusWithEtaList.includes(tripLegStatus as TripLegStatus);
  };

  /* Start Button Click Handlers */
  const handleOpenResolveException = (
    evt: React.MouseEvent<HTMLElement>,
    trip: TripLegSearchResponseItem,
  ) => {
    evt.stopPropagation();
    getTripLeg(trip, () => {
      setDialogResolveOpen(true);
    });
  };

  const handleCloseResolveDialog = () => {
    setDialogResolveOpen(false);
  };

  const handleRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    navigate(
      `/passengers/${params.row.passengerUUID}/reservations/${params.row.reservationReferenceID}?tripLegID=${params.row.tripLegReferenceID}`,
    );
  };
  /* End Button Click Handlers */

  const getTripLeg = (
    {
      clientBC,
      reservationReferenceID,
      passengerUUID,
      passengerFirstName,
      passengerLastName,
      tripLegReferenceID,
      tripLegExceptionBC,
      passengerCareLevelBC,
      tripModeBC,
    }: TripLegSearchResponseItem,
    callback: () => void,
  ) => {
    setLoadingTripLegData(true);
    triplegService
      .getTripLeg({ tripLegReferenceID })
      .subscribe((tripLeg: SyncedTripLeg) => {
        setSelectedTripLeg({
          ...tripLeg,
          clientBC,
          reservationReferenceID,
          passengerUUID,
          passengerFirstName,
          passengerLastName,
          tripLegExceptionBC,
          passengerCareLevelBC,
          tripModeBC,
        });
        if (callback) {
          callback();
        }
        setLoadingTripLegData(false);
      });
  };

  /* Start Column Render Functions */
  const renderPickupTime = useCallback(
    (params: GridCellParams) => {
      const tripLeg = params.row;
      const tripId = tripLeg.tripLegReferenceID;
      let timeString = '';

      if (tripLeg.isWillCall) {
        // This is the old code that conformed to the original spec. Apparently they want it to just show "Will Call" ticket F1V2-588
        // if (tripLeg.appointmentDate) {
        //   // Since the date has no time, we parse it and add 11 hours to ensure it displays the expected date.
        //   // If we didn't do this it would display the previous day since it parsed as a UTC date.
        //   const m = DateTime.fromISO(tripLeg.appointmentDate)
        //     .setZone(dateUtils.WORK_TIME_ZONE)
        //     .plus({ hours: 11 });
        //   timeString = `${m.toLocaleString(DateTime.TIME_SIMPLE)}, ${t('reservation.willCallTime')}`;
        // }
        timeString = t('reservation.willCallTime');
      } else {
        if (tripLeg.pickUpDueTimeUTC) {
          const tz = timezones.find(
            (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
          );
          const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
            zone: tz?.ianaStandard,
          });
          timeString = m.toLocaleString(DateTime.TIME_SIMPLE);
        }
      }
      return (
        <CellDualValue
          primaryValue={timeString || '-'}
          secondaryValue={tripId || ''}
        />
      );
    },
    [t, timezones],
  );

  const renderPickUpAddress = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    return (
      <CellDualValue
        primaryValue={`${tripLeg.pickUpAddressLine1}`}
        secondaryValue={`${tripLeg.pickUpCity} ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode.substring(0, 5)}`}
      />
    );
  };

  const renderDropOffAddress = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    return (
      <CellDualValue
        primaryValue={`${tripLeg.dropOffAddressLine1}`}
        secondaryValue={`${tripLeg.dropOffCity} ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode.substring(0, 5)}`}
      />
    );
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const passengerFirstName = params.row.passengerFirstName;
    const passengerLastName = params.row.passengerLastName;

    return (
      <CellDualValue
        primaryValue={`${passengerFirstName} ${passengerLastName}`}
        secondaryValue={`${params.row.tripModeBC}${params.row.passengerCareLevelBC}`}
      />
    );
  };

  const renderProviderName = (params: GridCellParams): React.ReactElement => {
    let providerName = params.row.providerName;
    let driverName = params.row.driverName;

    return (
      <CellDualValue
        primaryValue={providerName}
        secondaryValue={
          driverName ? driverName : t('monitoring.noDriverAssigned')
        }
      />
    );
  };

  const renderAssignee = (params: GridCellParams): React.ReactElement => {
    const assignee =
      triplegUtils.getExceptionAssignmentUserName(params.row) ||
      t('triplegInventory.unassigned');
    if (!assignee) {
      return (
        <CellSingleValue>{t('triplegInventory.unassigned')}</CellSingleValue>
      );
    }

    return (
      <CellDualValue
        primaryValue={assignee}
        secondaryValue={params.row.assigneeRole || ''}
      />
    );
  };

  const renderTimeOnList = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const exceptionLog = tripLeg.tripLegExceptionLogs;

    if (!exceptionLog || exceptionLog.length === 0) {
      return <CellSingleValue>{'-'}</CellSingleValue>;
    }

    const sortedLogs = [...exceptionLog].sort(
      (a, b) =>
        DateTime.fromISO(a.createdOnUTC).toMillis() -
        DateTime.fromISO(b.createdOnUTC).toMillis(),
    );

    let startIndex = -1;
    for (let i = sortedLogs.length - 1; i >= 0; i -= 1) {
      const entry = sortedLogs[i];
      if (
        entry.tripLegExceptionID === null ||
        entry.tripLegExceptionID === undefined
      ) {
        startIndex = i + 1;
        break;
      }
    }

    if (startIndex === -1) {
      startIndex = sortedLogs.findIndex(
        (e) =>
          e.tripLegExceptionID !== null && e.tripLegExceptionID !== undefined,
      );
    }

    if (startIndex === -1 || startIndex >= sortedLogs.length) {
      return <CellSingleValue>{'-'}</CellSingleValue>;
    }

    const startEntry = sortedLogs[startIndex];
    const firstExceptionTime = DateTime.fromISO(startEntry.createdOnUTC);
    if (!firstExceptionTime.isValid) {
      return <CellSingleValue>{'-'}</CellSingleValue>;
    }

    const now = DateTime.now();
    const durationMillis = Math.abs(
      now.toMillis() - firstExceptionTime.toMillis(),
    );
    const totalMinutes = Math.floor(durationMillis / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const timeDisplay = `${hours}:${String(minutes).padStart(2, '0')}`;

    return <CellSingleValue>{timeDisplay}</CellSingleValue>;
  };

  const renderException = (params: GridCellParams): React.ReactElement => {
    const getExceptionName = (exceptionCode: string): string => {
      if (!exceptionCode) return '';

      switch (exceptionCode.toUpperCase()) {
        case 'NODRIVER':
          return t('tripLegException.noDriver');
        case 'DRIVERNOTONLINE':
          return t('tripLegException.driverNotOnline');
        case 'DRIVERLATE':
          return t('tripLegException.driverLate');
        case 'OPENSAMEDAY':
          return t('tripLegException.open');
        default:
          return exceptionCode;
      }
    };

    const showEta = shouldShowEta(params);
    const driverLocations = params.row.tripLegDriverLocations;
    let etaDisplay = '?';

    if (driverLocations && driverLocations.length > 0) {
      const etaList = driverLocations
        .map((location: { estimatedTimeOfArrivalSeconds: number }) =>
          formatEta(location.estimatedTimeOfArrivalSeconds),
        )
        .filter(Boolean);
      etaDisplay = etaList.length > 0 ? etaList.join(', ') : '?';
    }

    return (
      <CellDualValue
        primaryValue={getExceptionName(params.row.tripLegExceptionBC || '')}
        secondaryValue={
          showEta ? `${t('triplegInventory.eta')}: ${etaDisplay}` : ''
        }
      />
    );
  };

  const renderActions = (params: GridRenderCellParams) => {
    const isEscalated = params.row.isEscalated;
    return (
      <CellActions>
        {!isSuperReaderUser && !isEscalated && (
          <ButtonException
            onClick={(evt) => handleOpenResolveException(evt, params.row)}
            label={t('todaysTrips.resolveException')}
          />
        )}
      </CellActions>
    );
  };
  /* End Column Render Functions */

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('triplegInventory.pickup'),
      flex: 0.75,
      sortable: true,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickUpAddress',
      headerName: t('todaysTrips.pickupAddress'),
      flex: 1.5,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderPickUpAddress,
    },
    {
      field: 'dropOffAddress',
      headerName: t('todaysTrips.dropoffAddress'),
      flex: 1.5,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderDropOffAddress,
    },
    {
      field: 'passengerFirstName',
      headerName: t('triplegInventory.member'),
      flex: 1,
      renderCell: renderMember,
    },
    {
      field: 'transportationProviderName',
      headerName: t('triplegInventory.providerTitle'),
      flex: 1,
      renderCell: renderProviderName,
    },
    {
      field: 'assignee',
      headerName: t('triplegInventory.assignee'),
      flex: 1,
      sortable: false,
      renderCell: renderAssignee,
    },
    {
      field: 'timeOnList',
      headerName: t('triplegInventory.timeOnList'),
      flex: 1,
      sortable: false,
      renderCell: renderTimeOnList,
    },
    {
      field: 'tripLegExceptionBC',
      headerName: t('triplegInventory.exception'),
      flex: 1,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: renderActions,
    },
  ];

  const isMemberContactException = triplegUtils.isMemberContactExceptionBC(
    selectedTripLeg?.tripLegExceptionBC,
  );

  return (
    <>
      <DataGridPro
        getRowId={(row) => `${row.tripLegReferenceID}`}
        rows={tripLegs}
        rowCount={rowCount}
        columns={columns}
        pagination={true}
        paginationModel={{
          page: page - 1,
          pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
        }}
        onRowClick={handleRowClick}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={handleSortModelChange}
        autoHeight={true}
        loading={loading}
        disableRowSelectionOnClick={true}
        disableMultipleRowSelection={true}
      />
      {selectedTripLeg && dialogResolveOpen && (
        <ExceptionResolveDialog
          tripLeg={selectedTripLeg}
          open={dialogResolveOpen}
          close={handleCloseResolveDialog}
          userRoleBC="MER"
          isMemberContactException={isMemberContactException}
        />
      )}
    </>
  );
};

export { MonitoringExceptionsTable };
